import { Badge, Button, Color, Icon, Radius, Select, Spacing, Text, TextInput } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { StyleSheet, TouchableOpacity, View, ViewProps, useWindowDimensions } from 'react-native'
import { useFloors, useWorkspaces, Workspace } from 'zens-store'
import { FloorSelect } from 'zens-fragment'

import { Trans, useTranslation } from 'react-i18next'

import { FloorplanView, FloorplanZoomView } from '../floorplan'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  title: {
    marginBottom: Spacing.S,
  },
  search: {
    minWidth: 300,
  },
  head: {
    zIndex: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Spacing.S,
  },
  buttons: {
    flexDirection: 'row',
  },
  button: {
    marginLeft: Spacing.XS,
  },
  floorplan: {
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
    padding: Spacing.S,
  },
  footer: {
    marginTop: Spacing.M,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  skeletonButton: {
    minWidth: 0,
  },
  skeletonFloorplan: {
    backgroundColor: Color.simplyWhite,
  },
  skeletonSelect: {
    maxWidth: 200,
  },
  skeletonSearch: {
    minWidth: 200,
  },
  loading: {
    position: 'absolute',
    bottom: Spacing.M,
    right: Spacing.M,
  },
})

export interface ZoneWorkspace {
  workspace: Workspace
  zoneId?: number
}

export interface Props {
  companyId: number
  zoneId: number
  onSave?: (workspaces: ZoneWorkspace[]) => Promise<void>
  onCancel?: () => void
  floorId?: number
  onChangeFloor?: (floorId: number) => void
}

const ZoneAddWorkspacesFloorplan = (props: Props) => {
  const { companyId, zoneId = 0, onCancel, onSave, floorId = 0, onChangeFloor } = props
  const [search, setSearch] = useState('')
  const { height } = useWindowDimensions()
  const { t } = useTranslation()
  const floors = useFloors({ companyId })
  const workspaces = useWorkspaces({ companyId, filter: { floorId: [floorId] } }, { enabled: !!floorId })

  const workspacesSearch = useWorkspaces(
    { companyId, filter: { search }, pagination: { page: 0, size: 1000 } },
    { enabled: !!search },
  )
  const workspacesZone = useWorkspaces(
    { companyId, filter: { zoneId: [zoneId] }, pagination: { page: 0, size: 1000 } },
    { enabled: !!zoneId },
  )
  const [data, setData] = useState(workspacesZone.data?.data?.map((workspace) => ({ workspace, zoneId })) || [])

  function toggle(workspace: Workspace) {
    const found = data.find((item) => item.workspace.id === workspace.id)
    if (found) {
      setData(
        data.map((item) =>
          item.workspace.id === workspace.id
            ? {
                workspace,
                zoneId: item.zoneId === zoneId ? 0 : zoneId,
              }
            : item,
        ),
      )
    } else {
      setData([...data, { workspace, zoneId }])
    }
  }

  const workspaceItems = workspaces.data?.data
  const totalNumberOfWorkspaces = workspaceItems?.length

  function colorForWorkspace(workspace: Workspace) {
    const disabled = search && !workspacesSearch.data?.data.find((searchItem) => searchItem.id === workspace.id)
    return disabled
      ? Color.graphicGrey
      : data.find((selectedItem) => selectedItem.workspace.id === workspace.id && selectedItem.zoneId === zoneId)
        ? Color.greyBlue
        : Color.simplyWhite
  }

  function borderColorForWorkspace(workspace: Workspace) {
    const disabled = search && !workspacesSearch.data?.data.find((searchItem) => searchItem.id === workspace.id)
    return disabled ? Color.calmGrey : Color.premiumBlack
  }

  function selectWorkspace(workspace: Workspace) {
    const disabled = search && !workspacesSearch.data?.data.find((searchItem) => searchItem.id === workspace.id)
    return !disabled ? toggle(workspace) : undefined
  }

  useEffect(() => {
    setData(workspacesZone.data?.data.map((workspace) => ({ workspace, zoneId })) || [])
  }, [workspaceItems?.map((item) => item.id).join(',')])

  useEffect(() => {
    if (!floorId) {
      const firstFloor = floors.data?.[0]
      firstFloor && onChangeFloor?.(firstFloor.id)
    }
  }, [floorId])

  useEffect(() => {
    const floorContainsSearch = search
      ? workspacesSearch.data?.data.length === 0 ||
        workspacesSearch.data?.data.some((workspace) => workspace.floorId === floorId && workspace?.floorplanIsPlaced)
      : data.filter((item) => item.workspace.floorId === floorId).length > 0
    if (!floorContainsSearch) {
      const workspaceFloor = search
        ? workspacesSearch.data?.data.find((workspace) => workspace.floorplanIsPlaced)
        : data.find((item) => item.workspace.floorplanIsPlaced)?.workspace
      workspaceFloor?.floorId && onChangeFloor?.(workspaceFloor.floorId)
    }
  }, [workspacesSearch.data?.data])

  const save = () => {
    onSave?.(data)
  }

  function updateSearch(search: string) {
    setSearch(search)
  }

  const renderHeader = () => {
    return (
      <View style={styles.head}>
        <FloorSelect
          companyId={companyId}
          groupBuilding
          value={floorId}
          onChange={(value) => onChangeFloor?.(value as number)}
        />

        <TextInput
          left="search"
          placeholder={t('Search workspace name')}
          right={
            search ? (
              <TouchableOpacity onPress={() => updateSearch('')} testID="WorkspaceList.Search.Clear">
                <Icon name="close" size="S" />
              </TouchableOpacity>
            ) : undefined
          }
          value={search}
          onChangeText={updateSearch}
          style={styles.search}
          testID="WorkspaceList.Search"
        />
      </View>
    )
  }
  return (
    <View style={styles.container}>
      <Text level={4} style={styles.title}>
        <Trans>Add workspaces to zone</Trans>
      </Text>
      {renderHeader()}
      <View>
        <FloorplanZoomView
          style={[styles.floorplan, { height: height - 264 - Spacing.M }]}
          testID="InsightsLiveMap.MapView"
        >
          <FloorplanView
            companyId={companyId}
            floorId={floorId}
            data={workspaceItems}
            itemColor={(item) => colorForWorkspace(item as Workspace)}
            itemBorderColor={(item) => borderColorForWorkspace(item as Workspace)}
            onItemPress={(item) => selectWorkspace(item as Workspace)}
          />
        </FloorplanZoomView>
        {workspaces.isFetching && (
          <Badge
            title={t('Loading workspaces ({{loaded}} of {{total}})...', {
              loaded: workspaceItems?.length,
              total: totalNumberOfWorkspaces,
            })}
            style={styles.loading}
            status="inactive"
          />
        )}
      </View>
      <View style={styles.footer}>
        <Button title={t('Cancel')} size="L" light style={styles.button} onPress={onCancel} />
        <Button title={t('Save')} size="L" style={styles.button} onPress={save} testID="Workspaces.Save" />
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const { height } = useWindowDimensions()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Text level={4} style={styles.title}>
        {t('Add workspaces to zone')}
      </Text>
      <View style={styles.head}>
        <TextInput.Skeleton style={styles.skeletonSearch} />
      </View>
      <View style={styles.head}>
        <Select.Skeleton style={styles.skeletonSelect} />
        <View style={styles.buttons}>
          <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
          <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
        </View>
      </View>
      <View style={[styles.floorplan, { height: height - 264 - Spacing.M }, styles.skeletonFloorplan]} />
    </View>
  )
}
ZoneAddWorkspacesFloorplan.Skeleton = Skeleton
export default ZoneAddWorkspacesFloorplan
